import React, { useMemo } from 'react';

import VirtualTable, { VirtualTableType } from '@/views/ddp/components/VirtualTable/VirtualTable';
import { PlankSelectionScopeProvider } from '@/views/ddp/hooks/usePlankSelectionScope';
import usePlankTableStore from '@/views/ddp/hooks/usePlankTableStore';
import { PlankSelectionItem } from '@/views/ddp/state/types/PlankSelectionStore';
import { getCardWidthForType, getColumnWidthForType, getSidepanelToggleWidthForType } from '@/views/ddp/utils/table';

import TableTotal from '../TableTotal/TableTotal';

import styles from './TableList.module.css';

export type TableListType<T> = Omit<VirtualTableType<T>, 'height'> & {
  onTotalClick: () => void;
  /** Maps a row to the item it contributes to Ctrl/Shift multi-selection, or undefined when
   * that row is not selectable. Omit entirely for lists that do not support selection. */
  toSelectionItem?: (row: T) => PlankSelectionItem | undefined;
  totalTestId?: string;
  itemHeight?: number;
  dynamic?: boolean;
  tableHeight?: number;
  isDefect?: boolean;
};

/** Stable empty list, so columns without selection do not hand the scope a new array each
 * render. */
const NO_SELECTION_ITEMS: PlankSelectionItem[] = [];

const TableList = <T,>({
  data,
  onTotalClick,
  estimateSize = undefined,
  totalTestId = undefined,
  itemHeight = 58,
  dynamic = false,
  tableHeight = 207,
  isDefect = false,
  toSelectionItem = undefined,
  ...tableProps
}: TableListType<T>) => {
  const noColumns = usePlankTableStore((state) => state.getColumns().length);

  const colWidth = getColumnWidthForType(noColumns, isDefect);
  const toggleWidth = getSidepanelToggleWidthForType(noColumns, isDefect);
  const cardWidth = getCardWidthForType(noColumns, isDefect);

  const sizes = useMemo(() => {
    if (!dynamic || !estimateSize || !Array.isArray(data)) return [];
    const measure = estimateSize as (index: number) => number;
    return data.map((__, index) => measure(index));
  }, [dynamic, estimateSize, data]);

  /** Selection items for the whole column, so a Shift-range covers rows the virtualizer has
   * not mounted as well. */
  const selectionItems = useMemo(() => {
    if (!toSelectionItem || !Array.isArray(data)) return NO_SELECTION_ITEMS;
    return data.map(toSelectionItem).filter((item): item is PlankSelectionItem => item !== undefined);
  }, [data, toSelectionItem]);

  return (
    <PlankSelectionScopeProvider items={selectionItems}>
      <div
        className={styles.columnStyle}
        style={{ minWidth: `${colWidth}px`, '--plank-card-width': `${cardWidth}px` } as React.CSSProperties}
      >
        <VirtualTable
          data={data}
          estimateSize={estimateSize || (() => itemHeight + 2)}
          {...tableProps}
          className={styles.columnVirtListStyle}
          style={{ '--sidepanel-toggle-width': `${toggleWidth}px` } as React.CSSProperties}
          height={tableHeight}
        />
        <TableTotal
          data-testid={totalTestId}
          collapsed={tableProps?.collapse}
          onClick={onTotalClick}
          total={data ? data.length : 0}
          itemHeight={itemHeight}
          sizes={sizes}
        />
      </div>
    </PlankSelectionScopeProvider>
  );
};
export default TableList;
